import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EMPTY_STRING } from '../../../const/const';
import { ToolsService } from '../../../providers/tools.service';
import { MainService } from '../../../providers/main.service';
import { Roles } from '../../../const/const';
import { PaymentPlanResponse, PaymentPlanTransactions } from '../../../types/conversations/payment-plan.model';
import { PropertyManagementFeature } from '../../../types/property-management-feature';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import datesUtils from 'src/app/utils/dates.utils';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-payment-plan',
  templateUrl: './payment-plan.component.html',
  styleUrls: ['./payment-plan.component.scss'],
})
export class PaymentPlanComponent implements OnInit {
  transactions: PaymentPlanTransactions[] = [];
  public propertyManagementFeature = PropertyManagementFeature;
  public today = new Date();

  constructor(
    public dialogRef: MatDialogRef<PaymentPlanComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public http: HttpClient,
    public tools: ToolsService,
    public main: MainService
  ) {}

  public isError = false;
  public isReadOnly = true;
  public hasChanged = false;
  public canOfferPaymentPlans = true;

  public ngOnInit(): void {
    this.canOfferPaymentPlans = this.data?.canOfferPaymentPlans !== false;
    this.loadPayments();
  }

  private loadPayments(): void {
    this.transactions = structuredClone(this.data?.paymentPlan?.transactions || []);
  }

  public addPaymentPlan(): void {
    if (!this.canOfferPaymentPlans) {
      return;
    }
    let collectedBalance = 0;
    this.transactions.forEach(({ balance }) => (collectedBalance += balance));
    const balance = this.data.balance - collectedBalance;
    this.transactions.push({ balance, date: EMPTY_STRING });
  }

  public removePaymentPlan(index: number): void {
    this.transactions.splice(index, 1);
  }

  public save(): void {
    if (!this.canOfferPaymentPlans) {
      return;
    }
    this.isError = false;
    const isValid = this.checkFormValues();
    if (!isValid) {
      this.isError = true;
      return;
    }
    const formattedTransactions = this.getFormattedTransactions();
    if (this.data.paymentPlan?._id) {
      this.updatePaymentPlan(formattedTransactions);
    } else {
      this.setPaymentPlan(formattedTransactions);
    }
  }

  private updatePaymentPlan(formattedTransactions: PaymentPlanTransactions[]): void {
    const data = {
      paymentPlanID: this.data.paymentPlan._id,
      transactions: formattedTransactions,
      modifiedByUserID: this.main.userID,
      modifiedByUserType: Roles.SUPERVISOR,
      modifiedDate: new Date(),
    };
    this.savePaymentPlan(
      this.http.put<{ data: PaymentPlanResponse }>(`${this.tools.NS_URL}/payment-plan`, data),
      'updated'
    );
  }

  private setPaymentPlan(formattedTransactions: PaymentPlanTransactions[]): void {
    const data = {
      tenantID: this.data.tenantID,
      transactions: formattedTransactions,
      createdByUserID: this.main.userID,
      createdByUserType: Roles.SUPERVISOR,
    };
    this.savePaymentPlan(
      this.http.post<{ data: PaymentPlanResponse }>(`${this.tools.NS_URL}/payment-plan`, data),
      'saved'
    );
  }

  private savePaymentPlan(request$: Observable<{ data: PaymentPlanResponse }>, action: string): void {
    request$.pipe(untilDestroyed(this)).subscribe({
      next: (res) => {
        this.transactions = res.data.transactions || [];
        this.data.paymentPlan = this.transactions.length ? structuredClone(res.data) : null;
        this.hasChanged = true;
        this.isReadOnly = true;
        this.tools.toast(`Payment plan was successfully ${action}`);
      },
      error: (err: HttpErrorResponse) => {
        this.tools.toast(err?.error || 'Something went wrong');
      },
    });
  }

  private checkFormValues(): boolean {
    let isValid = true;
    if (this.transactions.length > 0) {
      for (const transaction of this.transactions) {
        if (!transaction.balance || !transaction.date) {
          isValid = false;
        }
      }
    }
    return isValid;
  }

  public doCancel(): void {
    this.isReadOnly = true;
    this.isError = false;
    this.loadPayments();
  }

  private getFormattedTransactions(): PaymentPlanTransactions[] {
    return this.transactions.map((transaction) => ({
      ...transaction,
      date: transaction.date ? datesUtils.getUTCDateInYearMonthDayFormatAsString(transaction.date) : transaction.date,
    }));
  }
}
